/**
 * Экран долга: недоимки, пеня, ограничения и путь выхода (§Э5, §15).
 */

import {
  formatCrystals,
  formatDateTime,
  formatDuration,
  type LocaleCode,
} from "@hive/i18n";
import { crystalsToMilli, dailyPenalty, debtLimitMilli } from "@hive/shared";
import { useTranslation } from "react-i18next";

import type { DebtItemView, ServerWalletView } from "../api/wallet.js";
import { Crystals } from "./CrystalIcon.jsx";

export interface DebtScreenProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly level: number;
  readonly wallet: ServerWalletView;
  readonly debts: readonly DebtItemView[];
  readonly loading?: boolean;
}

export function DebtScreen({
  locale,
  timeZone,
  level,
  wallet,
  debts,
  loading = false,
}: DebtScreenProps): React.JSX.Element {
  const { t } = useTranslation("economy");

  const walletMilli = {
    hive: crystalsToMilli(wallet.hive),
    cash: crystalsToMilli(wallet.cash),
    debt: crystalsToMilli(wallet.debt),
    held: crystalsToMilli(wallet.held),
  };
  const penaltyMilli = dailyPenalty(walletMilli, level);
  const limitCrystals = Math.floor(debtLimitMilli(level) / 1000);

  if (wallet.debt <= 0 && debts.length === 0) {
    return (
      <div className="screen screen--debt">
        <header className="screen__head">
          <h1 className="screen__title">{t("debt.screen_title")}</h1>
        </header>
        <p className="screen__empty">{t("debt.repaid")}</p>
      </div>
    );
  }

  return (
    <div className="screen screen--debt">
      <header className="screen__head">
        <h1 className="screen__title">{t("debt.screen_title")}</h1>
      </header>

      <section className="debt-overview">
        <p className="debt-overview__amount">
          <Crystals>
            {t("debt.amount", { amount: formatCrystals(locale, walletMilli.debt, "hive") })}
          </Crystals>
        </p>
        <p className="debt-overview__limit">
          <Crystals>
            {t("debt.limit", { amount: formatCrystals(locale, limitCrystals * 1000, "hive") })}
          </Crystals>
        </p>
        <p className="debt-overview__tier">{t(`debt.tier.${wallet.debtTier}`)}</p>
        {penaltyMilli > 0 && (
          <p className="debt-overview__penalty">
            <Crystals>
              {t("debt.penalty", {
                amount: formatCrystals(locale, penaltyMilli, "hive"),
              })}
            </Crystals>
          </p>
        )}
        {wallet.recoveryDaysLeft > 0 && (
          <p className="debt-overview__recovery">
            {t("debt.recovery_days", { count: wallet.recoveryDaysLeft })}
          </p>
        )}
        <p className="debt-overview__way">{t("debt.way_out_hint")}</p>
        {wallet.debtTier === "shadow" && (
          <p className="debt-overview__restrict">{t("debt.restrict.shadow")}</p>
        )}
        {wallet.debtTier === "arrest" && (
          <p className="debt-overview__restrict">{t("debt.restrict.arrest")}</p>
        )}
      </section>

      <section className="debt-list" aria-label={t("debt.list_title")}>
        <h2 className="screen__section-title">{t("debt.list_title")}</h2>
        {loading && debts.length === 0 ? (
          <p className="screen__empty">{t("debt.loading")}</p>
        ) : debts.length === 0 ? (
          <p className="screen__empty">{t("debt.list_empty")}</p>
        ) : (
          <ul className="debt-list__items">
            {debts.map((item) => {
              const due = new Date(item.dueAt);
              const remaining = Math.max(0, due.getTime() - Date.now());
              return (
                <li key={item.hexId} className="debt-list__row">
                  <div className="debt-list__main">
                    <span className="debt-list__hex">{item.hexId.slice(-8)}</span>
                    <span className="debt-list__owner">{item.ownerName}</span>
                  </div>
                  <div className="debt-list__meta">
                    <span className="debt-list__amount">
                      <Crystals>{formatCrystals(locale, item.amount * 1000, "hive")}</Crystals>
                    </span>
                    <span className="debt-list__due">
                      {t("debt.due", {
                        date: formatDateTime(locale, due, timeZone),
                        duration: formatDuration(locale, remaining),
                      })}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
